import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight, CheckCircle2, BadgePoundSterling, MapPinned, Eye, HandHeart,
  UserPlus, Inbox, Handshake, Truck, X,
} from "lucide-react";
import PublicHeader from "@/components/PublicHeader";
import { FitterJobCard } from "@/components/FitterJobCard";
import { SERVICE_LABEL } from "@/lib/fitters";
import { floorLabel } from "@/lib/api";
import { track, captureUtm } from "@/lib/analytics";
import { useUnit } from "@/context/UnitContext";
import { dimPairLabel } from "@/lib/units";
import Seo, { organizationSchema } from "@/seo/Seo";

const EXAMPLE_JOB = {
  id: "example",
  reference_code: "DEMO-CF-001",
  title: "Bedroom carpet fitting",
  job_type: "collection_fitting",
  general_location: "Stockport",
  distance_miles: 4,
  flooring_type: "carpet",
  room_width_m: 3.6,
  room_length_m: 3.2,
  services_required: [
    "flooring_collection", "carpet_fitting", "supply_underlay", "supply_grippers", "door_trimming",
  ],
  customer_availability: ["Tuesday", "Wednesday"],
  collection_required: true,
  fitter_payment_gbp: 140,
  is_demo: true,
};

const TRUST = [
  { icon: BadgePoundSterling, text: "No joining fee" },
  { icon: MapPinned, text: "Choose your working area" },
  { icon: Eye, text: "See the payment before accepting" },
  { icon: HandHeart, text: "Accept only the jobs you want" },
];

const STEPS = [
  { icon: UserPlus, title: "Create your fitter profile", body: "Tell us where you work and which flooring services you provide." },
  { icon: Inbox, title: "Receive suitable local jobs", body: "See the room details, collection requirements, location and fitter payment." },
  { icon: Handshake, title: "Accept the work you want", body: "There is no obligation to accept every job." },
  { icon: Truck, title: "Collect and fit", body: "Once a job is confirmed, receive the information needed to complete it." },
];

export default function Fitters() {
  const nav = useNavigate();
  const [showJob, setShowJob] = useState(false);

  useEffect(() => {
    captureUtm();
    track("fitter_page_viewed");
  }, []);

  function goJoin() {
    track("fitter_join_started", { from: "hero" });
    nav("/fitters/join");
  }
  function viewJob() {
    track("example_job_viewed");
    setShowJob(true);
  }
  function acceptExample() {
    track("example_job_accept_clicked");
    nav("/fitters/join?from=example-job");
  }

  return (
    <>
      <Seo
        title="Get local flooring jobs — join Floor-Match as a founding fitter"
        description="Independent carpet and flooring fitters: choose your area and the jobs that suit you. Floor-Match connects you with local customers buying discounted remnants who need collection and fitting. No joining fee for founding fitters."
        path="/fitters"
        keywords="flooring fitter jobs Manchester, carpet fitter work, local fitting jobs, carpet fitter Stockport, flooring installer jobs"
        structured_data={[organizationSchema()]}
      />
      <PublicHeader />
      <main className="min-h-screen fm-grain pb-24" data-testid="fitters-page">
        {/* HERO */}
        <section className="relative overflow-hidden bg-[#0F3D2E]" data-testid="fitters-hero">
          <div
            className="absolute inset-0 opacity-40"
            style={{ backgroundImage: "radial-gradient(circle at 12% 18%, rgba(224,89,21,0.28), transparent 45%), radial-gradient(circle at 88% 82%, rgba(42,90,59,0.7), transparent 42%)" }}
            aria-hidden="true"
          />
          <div className="relative max-w-6xl mx-auto px-4 sm:px-6 pt-12 sm:pt-20 pb-12 sm:pb-20">
            <div className="max-w-3xl space-y-6">
              <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#f7c9ac] bg-[#E05915]/20 border border-[#E05915]/40 px-3 py-1.5 rounded-full">
                Founding fitter network
              </span>
              <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.05] tracking-tight">
                Get local flooring jobs with FloorMatch
              </h1>
              <p className="text-lg sm:text-xl text-white/85 max-w-2xl">
                Choose the areas you cover and the jobs that suit you. FloorMatch helps
                customers find discounted flooring and connects them with local professionals
                who can collect and fit it.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 pt-1">
                <button
                  onClick={goJoin}
                  className="inline-flex items-center justify-center gap-2 h-13 px-7 py-3.5 rounded-full bg-[#E05915] hover:bg-[#c24b10] text-white font-semibold text-base shadow-lg shadow-[#E05915]/30 transition-colors"
                  data-testid="hero-join-btn"
                >
                  Join as a founding fitter <ArrowRight className="w-4 h-4" aria-hidden="true" />
                </button>
                <button
                  onClick={viewJob}
                  className="inline-flex items-center justify-center gap-2 h-13 px-7 py-3.5 rounded-full bg-white/10 border border-white/25 text-white font-semibold text-base hover:bg-white/15 transition-colors"
                  data-testid="hero-example-btn"
                >
                  See an example job
                </button>
              </div>
              <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-x-6 gap-y-3 pt-4">
                {TRUST.map((t) => (
                  <span key={t.text} className="flex items-center gap-2 text-sm text-white/90">
                    <t.icon className="w-4 h-4 text-[#9fe0b6]" aria-hidden="true" /> {t.text}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* HOW IT WORKS */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-14 sm:pt-20" data-testid="fitters-how">
          <h2 className="font-display font-bold text-3xl sm:text-4xl text-slate-900">How it works</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
            {STEPS.map((s, i) => (
              <div key={s.title} className="bg-white border border-slate-200 rounded-2xl p-6">
                <div className="flex items-center gap-3">
                  <span className="grid place-items-center w-10 h-10 rounded-lg bg-[#0F3D2E] text-white">
                    <s.icon className="w-5 h-5" aria-hidden="true" />
                  </span>
                  <span className="font-display font-bold text-2xl text-slate-200">{i + 1}</span>
                </div>
                <h3 className="font-display font-semibold text-base text-slate-900 mt-4">{s.title}</h3>
                <p className="text-sm text-slate-600 mt-1.5">{s.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* EXAMPLE JOB */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-14 sm:pt-20" data-testid="fitters-example">
          <div className="grid lg:grid-cols-12 gap-8 items-start">
            <div className="lg:col-span-5">
              <p className="text-xs font-semibold uppercase tracking-wider text-[#E05915]">See it in action</p>
              <h2 className="font-display font-bold text-3xl sm:text-4xl text-slate-900 mt-1">
                The kind of job you'll receive
              </h2>
              <p className="text-slate-600 mt-3 max-w-md">
                Every job shows you the room, the services needed, the customer's availability and
                exactly what you'll be paid — before you accept. Retailer identity and the exact
                collection address stay private until a job is genuinely assigned to you.
              </p>
              <ul className="mt-5 space-y-2.5">
                {["See the payment up front", "No obligation to accept", "Local work, chosen by you"].map((t) => (
                  <li key={t} className="flex items-center gap-2 text-sm text-slate-700">
                    <CheckCircle2 className="w-4 h-4 text-[#2A5A3B]" aria-hidden="true" /> {t}
                  </li>
                ))}
              </ul>
            </div>
            <div className="lg:col-span-7">
              <FitterJobCard
                job={EXAMPLE_JOB}
                onView={viewJob}
                onAccept={acceptExample}
                acceptLabel="Accept example job"
                testId="example-job-card"
              />
            </div>
          </div>
        </section>

        {/* CLOSING CTA */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-14 sm:pt-20">
          <div className="rounded-3xl bg-[#2A5A3B] text-white px-6 sm:px-12 py-10 sm:py-14 text-center">
            <h2 className="font-display font-bold text-3xl sm:text-4xl">Ready to get local work?</h2>
            <p className="text-white/85 mt-3 max-w-xl mx-auto">
              Join the founding fitter network today. It takes a couple of minutes on your phone,
              and there's no joining fee.
            </p>
            <button
              onClick={goJoin}
              className="inline-flex items-center justify-center gap-2 h-13 px-8 py-3.5 rounded-full bg-[#E05915] hover:bg-[#c24b10] text-white font-semibold text-base mt-6 transition-colors"
              data-testid="cta-join-btn"
            >
              Join as a founding fitter <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </button>
          </div>
        </section>
      </main>

      {showJob && (
        <JobDetailPanel job={EXAMPLE_JOB} onClose={() => setShowJob(false)} onAccept={acceptExample} />
      )}
    </>
  );
}

function JobDetailPanel({ job, onClose, onAccept }) {
  const { unit } = useUnit();
  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      role="dialog"
      aria-modal="true"
      data-testid="job-detail-panel"
      onClick={onClose}
    >
      <div
        className="bg-white w-full sm:max-w-lg rounded-t-3xl sm:rounded-2xl max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-display font-semibold text-lg text-slate-900">{job.title}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="close-job-detail" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <Row label="Location" value={`${job.general_location} · approx ${job.distance_miles} miles away`} />
          {job.room_width_m && (
            <Row label="Room size" value={dimPairLabel(job.room_width_m, job.room_length_m, unit)} />
          )}
          <Row label="Flooring" value={floorLabel(job.flooring_type)} />
          <Row label="Collection" value={job.collection_required ? "Collect from a local FloorMatch retailer" : "Not required"} />
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Services required</div>
            <ul className="space-y-1.5">
              {job.services_required.map((s) => (
                <li key={s} className="flex items-center gap-2 text-sm text-slate-800">
                  <CheckCircle2 className="w-4 h-4 text-[#2A5A3B]" aria-hidden="true" /> {SERVICE_LABEL[s] || s}
                </li>
              ))}
            </ul>
          </div>
          <Row label="Customer availability" value={job.customer_availability.join(" or ")} />
          <div className="flex items-center justify-between bg-[#0F3D2E] text-white rounded-xl px-4 py-3">
            <span className="text-sm text-white/80">Fitter payment</span>
            <span className="font-display font-bold text-2xl">£{job.fitter_payment_gbp}</span>
          </div>
          <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
            Demonstration job — no real booking will be created.
          </p>
          <button
            onClick={onAccept}
            className="w-full h-12 rounded-full bg-[#E05915] hover:bg-[#c24b10] text-white font-semibold transition-colors"
            data-testid="detail-accept-btn"
          >
            Accept example job
          </button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-slate-100 pb-3">
      <span className="text-sm text-slate-500">{label}</span>
      <span className="text-sm font-semibold text-slate-900 text-right">{value}</span>
    </div>
  );
}
